import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth, type MeResponse } from '../lib/auth-context';
import { ApiError } from '../lib/api-client';

/**
 * D-051 — resolve destino pos-login com base em onboarding_completed.
 * Campo ausente = null = fallback para /mirror (comportamento atual seguro).
 */
function resolvePostLoginDestination(me: MeResponse | null): string {
  if (me !== null && me.onboarding_completed === false) return '/onboarding';
  return '/mirror';
}

export function LoginPage(): JSX.Element {
  const { login, hydrateMe } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || password.length === 0) {
      setError(t('errors.required'));
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      // D-051: roteamento inteligente — se backend expor onboarding_completed=false
      // redireciona para /onboarding; caso contrario vai para /mirror.
      // hydrateMe() ja foi chamado dentro de login() no auth-context.
      // Chamamos novamente para pegar o estado atual pos-login (incluindo o campo novo).
      const me = await hydrateMe();
      navigate(resolvePostLoginDestination(me), { replace: true });
    } catch (err) {
      // Mapa de erro -> frase humana. NUNCA mostrar codigo cru na tela: o owner
      // viu "unauthorized" em 2026-08-11 porque 401 caia no ramo generico.
      // 400 e 401 sao o MESMO fato para quem esta olhando: credencial errada.
      if (err instanceof ApiError) {
        if (err.status === 400 || err.status === 401) {
          setError(t('login.invalidCredentials'));
        } else if (err.status === 429) {
          setError(t('login.tooManyAttempts'));
        } else if (err.status >= 500) {
          setError(t('login.unavailable'));
        } else {
          setError(t('common.error'));
        }
      } else {
        // Sem resposta HTTP = rede/servidor fora. Dizer isso, e nao "erro".
        setError(t('login.unavailable'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page" role="main">
      <section className="auth-card">
        <h1>{t('login.title')}</h1>
        <p>{t('login.subtitle')}</p>

        <form onSubmit={onSubmit} noValidate>
          <label htmlFor="login-email">{t('login.emailLabel')}</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            aria-invalid={error !== null}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="login-password">{t('login.passwordLabel')}</label>
          <div className="auth-password-row">
            <input
              id="login-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              aria-invalid={error !== null}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-pressed={showPassword}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </button>
          </div>

          {error !== null ? (
            <p role="alert" className="error">
              {error}
            </p>
          ) : null}

          <button type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : t('login.submit')}
          </button>
        </form>

        <p>
          <Link to="/signup">{t('login.noAccount')}</Link>
        </p>
      </section>
    </main>
  );
}
